/**
 * Iter 96 — clickable KPI summary cards (spec §2). Fetches the server-side
 * /api/ops/summary (vendor-scoped; admin can filter by vendor). Each card
 * navigates to the corresponding filtered section via onNavigate(key).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import Money from "@/components/Money";
import { Link } from "react-router-dom";
import {
  CalendarCheck2, LogIn, LogOut, AlarmClockOff, KeyRound, Hourglass,
  Wallet, Car, CarFront, Wrench, TrendingUp, CalendarDays, Zap,
} from "lucide-react";

const CARDS = [
  { key: "reservations_today", label: "Reservations Today", icon: CalendarCheck2, nav: "today",   tone: "text-blue-600 bg-blue-50 ring-blue-100" },
  { key: "pickups_today",      label: "Pickups Today",      icon: LogIn,          nav: "pickups", tone: "text-emerald-600 bg-emerald-50 ring-emerald-100" },
  { key: "returns_today",      label: "Returns Today",      icon: LogOut,         nav: "returns", tone: "text-amber-600 bg-amber-50 ring-amber-100" },
  { key: "overdue_returns",    label: "Overdue Returns",    icon: AlarmClockOff,  nav: "overdue", tone: "text-rose-600 bg-rose-50 ring-rose-100", alertWhenNonZero: true },
  { key: "active_rentals",     label: "Active Rentals",     icon: KeyRound,       nav: "active",  tone: "text-indigo-600 bg-indigo-50 ring-indigo-100" },
  { key: "pending_reservations", label: "Pending Reservations", icon: Hourglass,  nav: "pending", tone: "text-sky-600 bg-sky-50 ring-sky-100" },
  { key: "awaiting_payment",   label: "Awaiting Payment",   icon: Wallet,         nav: "awaiting_payment", tone: "text-yellow-600 bg-yellow-50 ring-yellow-100" },
  { key: "vehicles_available", label: "Vehicles Available", icon: Car,            nav: "vehicles", tone: "text-teal-600 bg-teal-50 ring-teal-100" },
  { key: "vehicles_rented",    label: "Vehicles Rented",    icon: CarFront,       nav: "vehicles", tone: "text-violet-600 bg-violet-50 ring-violet-100" },
  { key: "vehicles_maintenance", label: "Under Maintenance", icon: Wrench,        nav: "vehicles", tone: "text-orange-600 bg-orange-50 ring-orange-100" },
  { key: "revenue_today",      label: "Revenue Today",      icon: TrendingUp,     nav: "revenue", tone: "text-emerald-700 bg-emerald-50 ring-emerald-100", money: true },
  { key: "revenue_month",      label: "Revenue This Month", icon: CalendarDays,   nav: "revenue", tone: "text-green-700 bg-green-50 ring-green-100", money: true },
];

export default function OpsSummaryCards({ role = "vendor", vendors = [], onNavigate }) {
  const [data, setData] = useState(null);
  const [vendorId, setVendorId] = useState("");

  useEffect(() => {
    let alive = true;
    const url = vendorId ? `/ops/summary?vendor_id=${vendorId}` : "/ops/summary";
    api.get(url).then((r) => { if (alive) setData(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [vendorId]);

  return (
    <div className="mb-6" data-testid="ops-summary">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-[0.14em] font-bold text-neutral-400">
          Today at a glance{data?.date ? ` · ${data.date}` : ""}
        </div>
        <div className="flex items-center gap-2">
        <Link
          to="/dashboard/today"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-neutral-900 text-white text-xs font-bold hover:bg-neutral-800 transition"
          data-testid="open-today-ops"
        >
          <Zap className="h-3.5 w-3.5" /> Today ops
        </Link>
        {role === "admin" && vendors.length > 0 && (
          <select
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            className="text-xs rounded-full border border-border bg-white px-3 py-1.5 font-semibold text-neutral-700"
            data-testid="ops-summary-vendor-filter"
          >
            <option value="">All Vendors</option>
            {vendors.map((v) => (
              <option key={v.id} value={v.id}>{v.business_name || v.full_name || v.name || v.email}</option>
            ))}
          </select>
        )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
        {CARDS.map((c) => {
          const raw = data ? data[c.key] : null;
          const Icon = c.icon;
          const alert = c.alertWhenNonZero && Number(raw) > 0;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => onNavigate?.(c.nav)}
              className={`text-left rounded-2xl border bg-white p-3.5 sm:p-4 hover:shadow-md active:scale-[0.98] transition ${
                alert ? "border-rose-300 ring-2 ring-rose-100" : "border-border"
              }`}
              data-testid={`ops-card-${c.key}`}
            >
              <span className={`inline-flex h-8 w-8 rounded-xl items-center justify-center ring-1 mb-2 ${c.tone}`}>
                <Icon className="h-4 w-4" />
              </span>
              <div className={`text-xl sm:text-2xl font-display font-bold tabular-nums ${alert ? "text-rose-600" : ""}`} data-testid={`ops-value-${c.key}`}>
                {raw == null ? "—" : c.money ? <Money amount={raw} /> : raw}
              </div>
              <div className="text-[11px] sm:text-xs text-neutral-500 font-semibold mt-0.5 leading-tight">{c.label}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
